import { type User } from '@/store/authStore';
import { generateCorrelationIdForBFF } from '@/utils/common';
import { getServiceAPIBaseURL } from '@/utils/env';

export type SessionResponse = User | null;

export type ChallengeType = 'LOGIN_OTP' | 'VERIFICATION_OTP' | 'SIGN_UP';

export type FailResultReason = 'VALIDATION' | 'CODE_MISMATCH' | 'EXPIRED_CODE' | 'RESEND_EXCEEDED';

export type ChallengeResult = {
  type: 'CHALLENGE';
  session: string;
  challengeType: ChallengeType;
};

export type FailResult = {
  type: 'FLAG';
  reason: FailResultReason;
};

export type SuccessResult = {
  type: 'SUCCESS';
  user: User;
};

export type AuthenticationResult = ChallengeResult | FailResult | SuccessResult;

export interface SignUpPayload {
  email: string;
  lastname: string;
  firstname: string;
  policyNumber: string;
}

export interface OtpPayload {
  otp: string;
  email: string;
  session: string;
}

export interface ResendCodePayload {
  email: string;
  challengeType: ChallengeType;
}

const baseUrl = getServiceAPIBaseURL();

function generateAuthRequestHeaders(): Record<string, string> {
  return {
    'x-correlation-id': generateCorrelationIdForBFF(),
    'content-type': 'application/json',
    'x-csrf': '1',
  };
}

export async function checkSession(): Promise<SessionResponse> {
  try {
    const response = await fetch(`${baseUrl}/auth/session`, {
      method: 'GET',
      credentials: 'include',
      headers: generateAuthRequestHeaders(),
    });

    if (response.ok) {
      return response.json() as unknown as SessionResponse;
    }

    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function signIn(email: string): Promise<AuthenticationResult> {
  const response = await fetch(`${baseUrl}/auth/sign-in`, {
    method: 'POST',
    credentials: 'include',
    headers: generateAuthRequestHeaders(),
    body: JSON.stringify({ email }),
  });

  if (response.ok) {
    return response.json() as unknown as AuthenticationResult;
  }

  throw new Response('Unauthorized', { status: 401 });
}

export async function signUp(payload: SignUpPayload): Promise<AuthenticationResult> {
  const response = await fetch(`${baseUrl}/auth/sign-up`, {
    method: 'POST',
    credentials: 'include',
    headers: generateAuthRequestHeaders(),
    body: JSON.stringify(payload),
  });

  if (response.ok) {
    return response.json() as unknown as AuthenticationResult;
  }

  throw new Response('Unauthorized', { status: 401 });
}

export async function verifyOTP(payload: OtpPayload): Promise<AuthenticationResult> {
  const response = await fetch(`${baseUrl}/auth/sign-in/otp`, {
    method: 'POST',
    credentials: 'include',
    headers: generateAuthRequestHeaders(),
    body: JSON.stringify(payload),
  });

  if (response.ok) {
    return response.json() as unknown as AuthenticationResult;
  }

  throw new Response('Unauthorized', { status: 401 });
}

export async function verifySignupOTP(payload: OtpPayload): Promise<AuthenticationResult> {
  const response = await fetch(`${baseUrl}/auth/sign-up/otp`, {
    method: 'POST',
    credentials: 'include',
    headers: generateAuthRequestHeaders(),
    body: JSON.stringify(payload),
  });

  if (response.ok) {
    return response.json() as unknown as AuthenticationResult;
  }

  throw new Response('Unauthorized', { status: 401 });
}

export async function signOut(): Promise<void> {
  const response = await fetch(`${baseUrl}/auth/sign-out`, {
    method: 'POST',
    credentials: 'include',
    headers: generateAuthRequestHeaders(),
  });

  if (response.ok) {
    return;
  }

  throw 'Signout failed';
}

export async function resendCode(payload: ResendCodePayload): Promise<AuthenticationResult> {
  const response = await fetch(`${baseUrl}/auth/resend-code`, {
    method: 'POST',
    credentials: 'include',
    headers: generateAuthRequestHeaders(),
    body: JSON.stringify(payload),
  });

  if (response.ok) {
    return response.json() as unknown as AuthenticationResult;
  }

  throw 'Cannot resend code';
}
